import { createZodDto } from "nestjs-zod/dto";
import { z } from "zod";

/**
 * GTM §6 — the ONLY funnel events the client may report directly.
 *
 * Every other funnel stage (signup, resume_created, resume_scored,
 * job_feed_viewed, subscribed) is tracked server-side where the action
 * actually happens; a client-writable enum wider than this would let any
 * authenticated user fabricate funnel stages.
 */
const clientFunnelEventBaseSchema = z.object({
  // `onboarding_step` is a diagnostic breadcrumb (NOT a funnel stage — the
  // funnel report ignores it); it exists to show where users abandon the
  // wizard. Keep this list minimal: anything here is client-fabricatable.
  // `pricing_viewed` is client-reported for the same reason as
  // `checkout_started`: the pricing page is a pure client route with no
  // server-side moment. Worst case a user inflates their own pricing views —
  // harmless, and the funnel dedups to one per user per day.
  // `paywall_viewed` is the same story for the global `<PaywallDialog>`: it
  // renders purely client-side off a zustand store, so there is no
  // server-side moment to hook. Worst case a user inflates their own
  // paywall-view count — harmless, and dedups to one per user per day.
  // `tailor_nudge_viewed` / `tailor_nudge_clicked` measure the dashboard
  // "Tailor your best match" card for in-house trialists. Diagnostics, not
  // funnel stages; the worst a client can do is inflate its own counts.
  // `onboarding_import_failed` records WHY a résumé import failed in the wizard
  // (props: code, fileType, fileSizeKb; never the file name or content). A
  // diagnostic, not a funnel stage.
  // `onboarding_import_skipped` records the no-file escape or the quick starter
  // résumé after import failure (props: reason only, "no_file", "after_error",
  // or "starter_resume"). Also a diagnostic.
  // `checkout_open_result` records HOW a checkout opened after `checkout_started`
  // (props: surface, plan, interval, method "overlay" | "hosted" | "hosted_fallback", ok,
  // optional short code; no PII). A diagnostic, not a funnel stage.
  // `offer_declined` records a one-tap reason after an offer is dismissed
  // (props: surface, reason, plan?, interval?; no PII). Diagnostic, not a funnel stage.
  // `offer_dismissed` records that a modal offer was closed without a checkout (props:
  // surface, plan, interval, via, dwellSeconds). It fires whether or not the user then
  // answers the reason question, so a dismissal that never reaches `offer_declined` is
  // still visible. Diagnostic, not a funnel stage.
  // `tailor_requested` (props: surface, jobId, optional placement) fires on every
  // USER-initiated tailor action in the signed-in app (first-match spotlight, feed card,
  // job detail), never for cron/auto-created applications. `placement` names the
  // control inside a surface ("first_tailor_step": the first-time step at the top of
  // the job detail), so its effect can be measured apart from the regular button. `first_match_spotlight_viewed` records the
  // "Start here" card shown to a user arriving from onboarding.
  // `first_match_spotlight_waiting` records its "Finding your first matches" state
  // (no props), so a user who saw it and left is visible. `first_match_spotlight_skipped`
  // (props: reason) records an onboarding arrival whose spotlight could not show.
  // Diagnostics only.
  // `feed_broaden_suggestion_shown` / `feed_broaden_suggestion_clicked` measure the
  // opt-in "Broaden this search" suggestion on a thin or empty feed (props: surface,
  // currentCount, plus which variants were offered / which one was clicked). Diagnostics only.
  // `upgrade_prompt_viewed` (props: surface) records an unprompted, non-blocking
  // upgrade message being shown: the ended-trial dashboard card, the priced Pro
  // note under Tailor in the job detail, the "Keep Pro" block in the application
  // kit, or saved tailored CV offers. A diagnostic, not a funnel stage.
  // `tailored_review_viewed` (props: applicationId, origin, kitReady) records that a
  // user saw the tailored-application surface, either the finished kit
  // (kitReady true) or the in-progress state (false). `tailored_kit_action` (props:
  // applicationId, action) records which kit action a user took. Diagnostics only.
  event: z.enum([
    "checkout_started",
    "checkout_open_result",
    "checkout_confirmed",
    "checkout_confirmation_timed_out",
    "offer_declined",
    "offer_dismissed",
    "pricing_viewed",
    "paywall_viewed",
    "onboarding_step",
    "onboarding_import_failed",
    "onboarding_import_skipped",
    "tailor_nudge_viewed",
    "tailor_nudge_clicked",
    "landing_job_card_viewed",
    "landing_job_card_tailor_clicked",
    "tailor_requested",
    "first_match_spotlight_viewed",
    "first_match_spotlight_waiting",
    "first_match_spotlight_skipped",
    "upgrade_prompt_viewed",
    "tailored_review_viewed",
    "tailored_kit_action",
    "feed_broaden_suggestion_shown",
    "feed_broaden_suggestion_clicked",
  ]),
  props: z.record(z.union([z.string(), z.number(), z.boolean()])).optional(),
});

/** Longest job id a client may report; real ids are 25-char cuids, so this is generous. */
const MAX_REPORTED_JOB_ID_LENGTH = 64;

/** Where a user-initiated tailor action started (`tailor_requested.surface`). */
export const TAILOR_REQUEST_SURFACES = [
  "first_match_spotlight",
  "feed_card",
  "job_detail",
] as const;

/** Which control inside the surface started it (`tailor_requested.placement`, optional). */
export const TAILOR_REQUEST_PLACEMENTS = ["first_tailor_step"] as const;

/** Why an onboarding arrival got no spotlight (`first_match_spotlight_skipped.reason`). */
export const FIRST_MATCH_SKIP_REASONS = [
  "already_tailored",
  "filtered_feed",
  "empty_feed_search_done",
  "feed_error",
  "not_first_page",
] as const;
/** Where an unprompted upgrade message was shown (`upgrade_prompt_viewed.surface`). */
export const UPGRADE_PROMPT_SURFACES = [
  "ended_trial_dashboard",
  "checkout_resume",
  "application_kit_checkout_resume",
  "tailored_review_checkout_resume",
  "job_detail_price_note",
  // Retained for historical events; the kit's post-action offer uses the next surface.
  "application_kit",
  "application_kit_first_value",
  "tailored_artifact_kit",
  "tailored_artifact_download",
  // Price and upgrade surfaces that used to show without any view event. Each value
  // equals the `source` its `UpgradeButton` already reports on `checkout_started`, so a
  // surface's views and its checkout clicks join on one name.
  "trial_banner",
  "jobs_feed_preview",
  "jobs_feed_unpaid",
  "dashboard_banner",
  "dashboard_expired_match",
  "auto_apply_cap",
  "applications_empty",
  "stats_paywall",
  "onboarding_ready_weekly",
  "onboarding_plan_picker",
  "settings_billing",
  "settings_billing_ultimate",
  "settings_subscription_hero_trial_ended",
  "integrations",
  "checkout_retry",
  "sidebar",
  "usage_meter",
  "usage_meter_keep_pro",
  "resume_cap",
  // Dialogs and pages whose own view event (paywall_viewed, pricing_viewed) is a different
  // funnel stage; they also report here so every price surface shares one event.
  "trial_artifact_offer",
  "paywall_dialog",
  "trial_keep_pro_dialog",
  "pricing_page",
] as const;

/** How an offer dialog was dismissed (`offer_dismissed.via`). */
export const OFFER_DISMISS_VIA = ["not_now", "close"] as const;

/** Longest dwell an `offer_dismissed` row reports; a tab left open overnight is not a dwell. */
export const MAX_OFFER_DWELL_SECONDS = 3600;

/** How the tailored-application drawer was reached (`tailored_review_viewed.origin`). */
export const TAILORED_REVIEW_ORIGINS = ["onboarding_landing", "feed", "other"] as const;

/** Kit actions a user can take (`tailored_kit_action.action`). */
export const TAILORED_KIT_ACTIONS = [
  "download_pdf",
  "copy_cover_letter",
  "open_job_posting",
  "mark_applied",
  "keep_pro_clicked",
] as const;

/** Where the broaden suggestion showed (`feed_broaden_suggestion_*.surface`). */
export const BROADEN_SUGGESTION_SURFACES = ["empty", "thin"] as const;

/** Which relaxation a broaden suggestion offers (`feed_broaden_suggestion_clicked.kind`). */
export const BROADEN_SUGGESTION_KINDS = [
  "country",
  "related_titles",
  "country_and_related_titles",
] as const;

/** Exact matches the feed held when the suggestion showed; the trigger is under 10. */
const broadenCurrentCountSchema = z.number().int().min(0).max(1000);

const reportedJobIdSchema = z.string().min(1).max(MAX_REPORTED_JOB_ID_LENGTH);

/**
 * Event-specific props, checked on top of the generic record above. Strict: an
 * unknown prop is rejected so these rows stay clean to query.
 */
const eventPropsSchemas: Partial<Record<string, z.ZodTypeAny>> = {
  tailor_requested: z
    .object({
      surface: z.enum(TAILOR_REQUEST_SURFACES),
      jobId: reportedJobIdSchema,
      placement: z.enum(TAILOR_REQUEST_PLACEMENTS).optional(),
    })
    .strict(),
  first_match_spotlight_viewed: z.object({ jobId: reportedJobIdSchema }).strict(),
  first_match_spotlight_waiting: z.object({}).strict().optional(),
  first_match_spotlight_skipped: z.object({ reason: z.enum(FIRST_MATCH_SKIP_REASONS) }).strict(),
  feed_broaden_suggestion_shown: z
    .object({
      surface: z.enum(BROADEN_SUGGESTION_SURFACES),
      currentCount: broadenCurrentCountSchema,
      country: z.boolean(),
      titles: z.boolean(),
      both: z.boolean(),
    })
    .strict(),
  feed_broaden_suggestion_clicked: z
    .object({
      surface: z.enum(BROADEN_SUGGESTION_SURFACES),
      currentCount: broadenCurrentCountSchema,
      kind: z.enum(BROADEN_SUGGESTION_KINDS),
      count: z.number().int().min(0).max(1000),
    })
    .strict(),
  upgrade_prompt_viewed: z.object({ surface: z.enum(UPGRADE_PROMPT_SURFACES) }).strict(),
  offer_dismissed: z
    .object({
      surface: z.string().regex(/^[a-z][\d_a-z]{0,63}$/),
      plan: z.enum(["PRO", "ULTIMATE"]),
      interval: z.enum(["weekly", "monthly", "yearly"]),
      via: z.enum(OFFER_DISMISS_VIA),
      dwellSeconds: z.number().int().min(0).max(MAX_OFFER_DWELL_SECONDS),
    })
    .strict(),
  tailored_review_viewed: z
    .object({
      applicationId: reportedJobIdSchema,
      origin: z.enum(TAILORED_REVIEW_ORIGINS),
      kitReady: z.boolean(),
    })
    .strict(),
  tailored_kit_action: z
    .object({ applicationId: reportedJobIdSchema, action: z.enum(TAILORED_KIT_ACTIONS) })
    .strict(),
};

export const clientFunnelEventSchema = clientFunnelEventBaseSchema.superRefine((value, ctx) => {
  const propsSchema = eventPropsSchemas[value.event];
  if (!propsSchema) return;
  const result = propsSchema.safeParse(value.props);
  if (result.success) return;
  for (const issue of result.error.issues) {
    ctx.addIssue({ ...issue, path: ["props", ...issue.path] });
  }
});

export class ClientFunnelEventDto extends createZodDto(clientFunnelEventSchema) {}
